import React, { useState, useEffect } from "react";
import { useOrganization } from "../contexts/OrganizationContext";
import padelEagleLogo from "../img/padelEagle.png";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import {
  InstallMobile as InstallIcon,
  Close as CloseIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  IosShare as ShareIcon,
  AddBox as AddBoxIcon,
} from "@mui/icons-material";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function PWAInstallPrompt() {
  const { currentOrg } = useOrganization();
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const appName = isPadelEagle ? "Padel Eagle" : "Stuttgart Padel League";
  const appShortName = isPadelEagle ? "Padel Eagle" : "SPL";

  // The static manifest is branded for SPL. For Padel Eagle, swap in an org-specific
  // manifest so the browser's install prompt and the installed app use the right name/icon.
  useEffect(() => {
    if (!isPadelEagle) return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const originalHref = link.href;
    const iconUrl = new URL(padelEagleLogo, window.location.origin).href;
    const manifest = {
      name: "Padel Eagle",
      short_name: "Padel Eagle",
      description: "Padel Eagle tournaments, results and player info",
      icons: [
        { src: iconUrl, type: "image/png", sizes: "512x512", purpose: "any" },
        { src: iconUrl, type: "image/png", sizes: "192x192", purpose: "any" },
      ],
      start_url: window.location.origin + "/",
      scope: window.location.origin + "/",
      display: "standalone",
      theme_color: "#743444",
      background_color: "#FAFAFA",
      orientation: "portrait-primary",
      categories: ["sports"],
      prefer_related_applications: false,
    };
    const blobUrl = URL.createObjectURL(
      new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" })
    );
    link.href = blobUrl;

    const appleTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    const appleTitleOriginal = appleTitle?.content;
    if (appleTitle) appleTitle.content = "Padel Eagle";
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const themeColorOriginal = themeColor?.content;
    if (themeColor) themeColor.content = "#743444";
    const appleIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    const appleIconOriginal = appleIcon?.href;
    if (appleIcon) appleIcon.href = iconUrl;

    return () => {
      link.href = originalHref;
      URL.revokeObjectURL(blobUrl);
      if (appleTitle && appleTitleOriginal !== undefined) appleTitle.content = appleTitleOriginal;
      if (themeColor && themeColorOriginal !== undefined) themeColor.content = themeColorOriginal;
      if (appleIcon && appleIconOriginal) appleIcon.href = appleIconOriginal;
    };
  }, [isPadelEagle]);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Detect Android
    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);

    const handler = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Check if user has dismissed before
      const dismissed = localStorage.getItem("pwa-install-dismissed");
      if (!dismissed) {
        setShowInstallDialog(true);
      }
      setShowInstallButton(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Check if already installed
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone;
    if (!isStandalone) {
      // Show button for iOS users even without beforeinstallprompt
      if (iOS) {
        setShowInstallButton(true);
      }
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // If no prompt available, just show the dialog
      setShowInstallDialog(true);
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("User accepted the install prompt");
    } else {
      console.log("User dismissed the install prompt");
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallDialog(false);
    setShowInstallButton(false);
  };

  const handleDismiss = () => {
    setShowInstallDialog(false);
    localStorage.setItem("pwa-install-dismissed", "true");
    // Don't hide the button completely, just the dialog
  };

  const handleManualInstall = () => {
    if (deferredPrompt) {
      handleInstallClick();
    } else {
      setShowInstallDialog(true);
    }
  };

  return (
    <>
      {/* Floating install button */}
      {showInstallButton && !showInstallDialog && (
        <IconButton
          color="primary"
          onClick={handleManualInstall}
          sx={{
            position: "fixed",
            bottom: { xs: 80, sm: 20 },
            right: { xs: 12, sm: 20 },
            zIndex: 1000,
            backgroundColor: "rgba(25, 118, 210, 0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(25, 118, 210, 0.3)",
            opacity: 0.6,
            transition: "all 0.3s ease",
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.25)",
              opacity: 1,
              transform: "scale(1.1)",
            },
          }}
        >
          <InstallIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      )}

      {/* Install dialog */}
      <Dialog
        open={showInstallDialog}
        onClose={handleDismiss}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <InstallIcon color="primary" />
              <Typography variant="h6">Install App</Typography>
            </Box>
            <IconButton onClick={handleDismiss} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {deferredPrompt ? (
            <>
              <Typography paragraph>
                Install the {appShortName} app on your device for quick access and offline
                functionality!
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body2" paragraph>
                  🚀 <strong>Quick Access:</strong> Launch directly from your
                  home screen
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  📱 <strong>Native Feel:</strong> Full-screen app experience
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  ⚡ <strong>Fast & Reliable:</strong> Optimized performance
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography paragraph>
                Add {appName} to your home screen for easy access!
              </Typography>

              {isIOS && (
                <Box
                  sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AppleIcon />
                    <Typography variant="subtitle1" fontWeight="bold">
                      For iPhone/iPad (Safari):
                    </Typography>
                  </Box>
                  <Box component="ol" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" paragraph>
                      Tap the{" "}
                      <ShareIcon
                        sx={{ fontSize: 16, verticalAlign: "middle" }}
                      />{" "}
                      <strong>Share</strong> button at the bottom of Safari
                    </Typography>
                    <Typography component="li" variant="body2" paragraph>
                      Scroll down and tap{" "}
                      <AddBoxIcon
                        sx={{ fontSize: 16, verticalAlign: "middle" }}
                      />{" "}
                      <strong>"Add to Home Screen"</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      Tap <strong>"Add"</strong> in the top right
                    </Typography>
                  </Box>
                </Box>
              )}

              {isAndroid && (
                <Box
                  sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AndroidIcon />
                    <Typography variant="subtitle1" fontWeight="bold">
                      For Android (Chrome):
                    </Typography>
                  </Box>
                  <Box component="ol" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" variant="body2" paragraph>
                      Tap the <strong>⋮</strong> menu button (top right)
                    </Typography>
                    <Typography component="li" variant="body2" paragraph>
                      Select <strong>"Install app"</strong> or{" "}
                      <strong>"Add to Home screen"</strong>
                    </Typography>
                    <Typography component="li" variant="body2">
                      Tap <strong>"Install"</strong>
                    </Typography>
                  </Box>
                </Box>
              )}

              {!isIOS && !isAndroid && (
                <Box
                  sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 2 }}
                >
                  <Typography variant="body2" paragraph>
                    <strong>Desktop browsers:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Look for an install icon in your browser's address bar, or
                    check your browser's menu for "Install {appName}" option.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismiss} color="inherit">
            {deferredPrompt ? "Maybe Later" : "Got it"}
          </Button>
          {deferredPrompt && (
            <Button
              onClick={handleInstallClick}
              variant="contained"
              color="primary"
            >
              Install Now
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PWAInstallPrompt;
